import React, { createContext, useContext, useRef } from 'react';

/**
 * What the page asks of the water beyond being calm. Sections write it
 * while they are on screen; the water reads it every frame it paints. A
 * ref rather than state: neither side should render React thirty times
 * a second to pass a few numbers.
 */
export type WaterStory = {
  /** 0 is calm water; 1 is the WEE story, as the app's home page tells it. */
  weight: number;
  /** The story clock, on the app's axis (wrapped). */
  t: number;
  /** Where the sunrise settles — the Enact house's centre, in CSS px from the viewport's top-left. */
  focus: { x: number; y: number } | null;
  /** 0 is calm water; 1 is the Result section's gentle swirl of the sunrise's colours. */
  swirl: number;
  /**
   * How we build's light, while its section is on screen: the lights going
   * down for the story's night, the dawn that follows it, and the bright
   * day the three levels stand in — each 0 to 1, already faded by how much
   * of the section shows. `focus` is where the dawn rises, in CSS px from
   * the viewport's top-left.
   */
  how: HowLight;
  /**
   * Whether the water answers the pointer. The landing page turns this
   * off while it is playing itself — the page is telling a story then,
   * and the water is part of it — and back on when it rests.
   */
  interactive: boolean;
  /**
   * Set by the water: paint one frame now. The water runs its own loop,
   * so this matters only where it is still (reduced motion) and a weight
   * has just changed under it.
   */
  repaint: (() => void) | null;
};

export type HowLight = {
  night: number;
  dawn: number;
  day: number;
  focus: { x: number; y: number } | null;
};

export const NO_HOW_LIGHT: HowLight = { night: 0, dawn: 0, day: 0, focus: null };

const calm = (): WaterStory => ({
  weight: 0,
  t: 0,
  focus: null,
  swirl: 0,
  how: NO_HOW_LIGHT,
  interactive: true,
  repaint: null,
});

const WaterStoryContext = createContext<React.MutableRefObject<WaterStory> | null>(null);

export const WaterStoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const story = useRef<WaterStory>(calm());
  return <WaterStoryContext.Provider value={story}>{children}</WaterStoryContext.Provider>;
};

/** The shared story. Outside a provider it is a private, calm one, so nothing breaks. */
export function useWaterStory(): React.MutableRefObject<WaterStory> {
  const fallback = useRef<WaterStory>(calm());
  return useContext(WaterStoryContext) ?? fallback;
}
